import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Gift, Loader2, CheckCircle, Sparkles } from "lucide-react";
import api from "@/utils/api";
import { tr } from "@/utils/i18n";

/**
 * Promo code redemption card.
 * - Lets users enter a code (e.g., LAUNCH30) to start a 30-day free trial
 *   with a credit card on file (no immediate charge).
 * - Disclaimer: "Won't be charged for X days. After that, normal Premium price. Cancel anytime."
 */
export function PromoCodeCard({ language, planId = "monthly", getAuthHeader, isAuthenticated, onAuthRequired }) {
  const [code, setCode] = useState("");
  const [validated, setValidated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  const handleValidate = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setValidated(null);
    try {
      const res = await api.post("/promo/validate", { code: code.trim() });
      setValidated(res.data);
      if (!res.data.valid) {
        toast.error(res.data.message || "Invalid promo code");
      }
    } catch {
      toast.error("Failed to check promo code");
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!isAuthenticated) {
      onAuthRequired?.();
      toast.error(tr(language, "Please log in first", "Faça login primeiro", "Inicia sesión primero"));
      return;
    }
    setRedeeming(true);
    try {
      const res = await api.post(
        "/promo/redeem",
        { code: code.trim(), plan_id: planId, origin_url: window.location.origin },
        { headers: getAuthHeader() }
      );
      // Redirect to Stripe
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to redeem promo");
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-violet-500/10 via-cyan-500/5 to-transparent border-violet-500/30" data-testid="promo-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
            <Gift className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">
              {tr(language, "Got a promo code?", "Tem um cupom?", "¿Tienes un código?")}
            </h3>
            <p className="text-xs text-zinc-500">
              {tr(language,
                "Unlock extended free trial with credit card on file",
                "Desbloqueie teste grátis estendido com cartão em arquivo",
                "Desbloquea prueba gratis extendida con tarjeta en archivo"
              )}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-zinc-400 text-xs">
              {tr(language, "Enter code", "Digite o código", "Introduce el código")}
            </Label>
            <div className="flex gap-2">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="LAUNCH30"
                className="bg-zinc-900 border-zinc-800 font-mono tracking-widest"
                data-testid="promo-code-input"
              />
              <Button
                onClick={handleValidate}
                variant="outline"
                disabled={loading || !code.trim()}
                data-testid="promo-validate-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : tr(language, "Check", "Verificar", "Verificar")}
              </Button>
            </div>
          </div>

          {validated?.valid && (
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg" data-testid="promo-valid-banner">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <Badge className="bg-green-500/20 text-green-400">
                  {validated.trial_days} {tr(language, "days free", "dias grátis", "días gratis")}
                </Badge>
              </div>
              <p className="text-sm text-zinc-300">{validated.message}</p>
            </div>
          )}

          <Button
            onClick={handleRedeem}
            disabled={!validated?.valid || redeeming}
            className="w-full bg-gradient-to-r from-violet-500 to-cyan-500 hover:from-violet-600 hover:to-cyan-600 text-white font-bold disabled:opacity-50"
            data-testid="promo-redeem-btn"
          >
            {redeeming ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            {tr(language, "Activate Free Trial", "Ativar Teste Grátis", "Activar Prueba Gratis")}
          </Button>

          {/* Small-print disclaimer per user spec */}
          <p className="text-[10px] leading-tight text-zinc-500 mt-2" data-testid="promo-disclaimer">
            {validated?.valid ? (
              tr(language,
                `Your card will not be charged for ${validated.trial_days} days. After that, the normal Premium membership monthly price applies. Cancel anytime.`,
                `Seu cartão não será cobrado por ${validated.trial_days} dias. Após isso, o preço normal da assinatura mensal Premium se aplica. Cancele a qualquer momento.`,
                `Tu tarjeta no se cobrará por ${validated.trial_days} días. Después, se aplicará el precio normal de la membresía Premium mensual. Cancela en cualquier momento.`
              )
            ) : (
              tr(language,
                "Card on file required. Won't be charged during the trial. Normal Premium monthly price after trial ends. Cancel anytime.",
                "Cartão em arquivo necessário. Não será cobrado durante o teste. Preço normal Premium mensal após o teste. Cancele a qualquer momento.",
                "Se requiere tarjeta en archivo. No se cobra durante la prueba. Precio Premium mensual normal después de la prueba. Cancela en cualquier momento."
              )
            )}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
